import { useState, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Settings, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import { usePrintSettings } from "@/contexts/PrintSettingsContext";

const PrintSettingsDialog = ({ open, onOpenChange }) => {
  const { settings, updateSettings, resetSettings } = usePrintSettings();
  const [localSettings, setLocalSettings] = useState(settings);
  
  // Sync local state when dialog opens - using useMemo for derivation
  const displaySettings = useMemo(() => {
    return open ? localSettings : settings;
  }, [open, localSettings, settings]);

  // Reset local state when dialog opens
  const handleOpenChange = useCallback((newOpen) => {
    if (newOpen) {
      setLocalSettings(settings);
    }
    onOpenChange(newOpen);
  }, [settings, onOpenChange]);

  const handleSave = () => {
    updateSettings(localSettings);
    toast.success("Print settings saved!");
    onOpenChange(false);
  };

  const handleReset = () => {
    resetSettings();
    setLocalSettings({
      companyName: "EAC",
      showLogo: true,
      headerColor: "#4338CA"
    });
    toast.info("Settings reset to defaults");
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Print Settings
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="companyName">Company Name</Label>
            <Input
              id="companyName"
              value={localSettings.companyName}
              onChange={(e) => setLocalSettings(prev => ({ ...prev, companyName: e.target.value }))}
              placeholder="Enter company name for printouts"
              data-testid="print-company-name-input"
            />
            <p className="text-xs text-muted-foreground">
              This name will appear on all printed documents and PDFs
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="headerColor">Header Color</Label>
            <div className="flex items-center gap-2">
              <Input
                id="headerColor"
                type="color"
                value={localSettings.headerColor}
                onChange={(e) => setLocalSettings(prev => ({ ...prev, headerColor: e.target.value }))}
                className="w-16 h-10 p-1 cursor-pointer"
                data-testid="print-header-color-input"
              />
              <Input
                value={localSettings.headerColor}
                onChange={(e) => setLocalSettings(prev => ({ ...prev, headerColor: e.target.value }))}
                placeholder="#4338CA"
                className="flex-1"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Color used for document headers and accents
            </p>
          </div>

          {/* Preview */}
          <div className="rounded-lg border p-4 bg-muted/30">
            <p className="text-xs text-muted-foreground mb-2">Preview:</p>
            <div 
              className="rounded p-3 text-white text-sm font-semibold"
              style={{ background: localSettings.headerColor }}
            >
              {localSettings.companyName || "Company Name"} - Document Title
            </div>
          </div>
        </div>

        <DialogFooter className="flex justify-between sm:justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={handleReset}
            className="gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              className="gap-2"
              data-testid="save-print-settings-btn"
            >
              <Save className="w-4 h-4" />
              Save Settings
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PrintSettingsDialog;
